/* istanbul ignore file */
import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronUpIcon } from '@chakra-ui/icons';
import { Box, Flex, List, ListIcon, ListItem, Text } from '@chakra-ui/react';
import { FormTranslationKeys, Page, PageType, SystemTranslationKeys } from '@common/entities';
import { NotificationStatus } from 'enums/notification-status';
import { RootState } from 'state/store';

import { Alert, CheckMark, Circle } from 'components/custom-icons';
import notification from 'components/notification';
import {
  useGetActiveStepIndex,
  useReconcileSkippedHiddenGuarantorPages,
  useSaveBlankPageData,
  useSaveOptionsPageData,
  useSaveSummaryPage,
  useSystemTranslation,
} from 'hooks';
import { useIsPageFullyHidden } from 'hooks/display-logic/useIsPageFullyHidden';
import { useSaveFloorPlanPageData } from 'hooks/floor-plans';
import { useSaveQuote } from 'hooks/quotes';
import { BlankPage } from 'types/page';
import { hasDueNowPaymentRequired } from 'utils/charge-utils';

interface VisiblePageInfo {
  visiblePageCount: number;
  displayStepIndex: number;
  currentPageName: string | undefined;
}

interface StepNavigatorProps {
  filteredPages: Page[];
  onClose: any;
  onVisiblePagesChange?: (info: VisiblePageInfo) => void;
  isNavigationLoading?: boolean;
  onNavigationStart?: () => void;
  onNavigationEnd?: () => void;
}

// Separate component to handle page visibility check with hooks
function PageNavigationItem({
  page,
  index,
  activeStepIndex,
  leasingJourneyErrors,
  onStepClick,
  getPageTextColor,
  onVisibilityChange,
  isNavigationLoading,
}: {
  page: Page;
  index: number;
  activeStepIndex: number;
  leasingJourneyErrors: (number | null)[];
  onStepClick: (targetPageId: string, newPageIndex: number) => void;
  getPageTextColor: (index: number) => string;
  onVisibilityChange: (pageId: string, isVisible: boolean) => void;
  isNavigationLoading: boolean;
}) {
  const { t, tSystem } = useSystemTranslation();
  // Always call the hook, but only use the result for Blank pages
  const isPageFullyHidden = useIsPageFullyHidden(
    page.type === PageType.Blank ? (page as BlankPage) : null,
  );

  const isVisible = !(page.type === PageType.Blank && isPageFullyHidden);

  // Report visibility changes to parent
  useEffect(() => {
    onVisibilityChange(page.id as string, isVisible);
  }, [page.id, isVisible, onVisibilityChange]);

  // Only hide Blank pages that are fully hidden
  if (!isVisible) {
    return null;
  }

  const stepErrors = leasingJourneyErrors[index] ?? null;

  const renderStepStatus = () => {
    if (activeStepIndex === index) {
      return <ListIcon as={Circle} color="#22313C" stroke="#22313C" mr={1} mt={2} boxSize="24px" />;
    }
    if (stepErrors !== null && stepErrors > 0) {
      return <ListIcon as={Alert} color="red.bright" mr={1} mt={2} boxSize="24px" />;
    }
    if (stepErrors === 0) {
      return <ListIcon as={CheckMark} fill="green.500" mr={1} mt={2} boxSize="24px" />;
    }
    return (
      <ListIcon as={Circle} color="white.base" stroke="gray.700" mr={1} mt={2} boxSize="24px" />
    );
  };

  const renderDescription = () => {
    if (stepErrors !== null && stepErrors > 0) {
      return (
        <Text fontSize="8px" fontWeight="normal" mt={1} color="red.800" pb={2}>
          {tSystem(SystemTranslationKeys.nav.incompleteFields, {
            count: stepErrors,
          })}
        </Text>
      );
    }
    return null;
  };

  return (
    <ListItem key={page.id} display="flex" alignItems="start" mb={2}>
      {renderStepStatus()}
      <Box
        as="button"
        onClick={() => !isNavigationLoading && onStepClick(page.id as string, index)}
        type="button"
        p={2}
        fontWeight={activeStepIndex === index ? '700' : '400'}
        flex={1}
        textAlign="left"
        color={getPageTextColor(index)}
      >
        {page.id ? t(FormTranslationKeys.page.name(page.id), page.name) : page.name}
        {renderDescription()}
      </Box>
    </ListItem>
  );
}

const ApplicationNavigation: React.FC<StepNavigatorProps> = ({
  filteredPages,
  onClose,
  onVisiblePagesChange,
  isNavigationLoading = false,
  onNavigationStart = () => undefined,
  onNavigationEnd = () => undefined,
}) => {
  const { t, tSystem } = useSystemTranslation();
  const navigate = useNavigate();
  const getActiveStepIndex = useGetActiveStepIndex();
  const activeStepIndex = getActiveStepIndex();
  const { orgId, propertyId } = useParams();

  const { leasingJourneyErrors } = useSelector((state: RootState) => state.navigation);

  const { formData, addOns, selectedSpecials } = useSelector((state: RootState) => ({
    formData: state.application.form,
    addOns: state.unitSelection.addOns,
    selectedSpecials: state.unitSelection.selectedSpecials,
  }));

  const { pages } = formData;

  // Track which pages are visible (determined by each PageNavigationItem)
  const [visiblePageIds, setVisiblePageIds] = React.useState<Set<string>>(new Set());

  const handleVisibilityChange = React.useCallback((pageId: string, isVisible: boolean) => {
    setVisiblePageIds((prev) => {
      const next = new Set(prev);
      if (isVisible) {
        next.add(pageId);
      } else {
        next.delete(pageId);
      }
      return next;
    });
  }, []);

  // Calculate visible page count and current step index from actual visibility
  const visiblePageCount = visiblePageIds.size;
  const currentPageId = pages[activeStepIndex]?.id;

  // Get visible pages in order (preserving filteredPages order)
  const orderedVisiblePages = React.useMemo(() => {
    return filteredPages.filter((page) => visiblePageIds.has(page.id as string));
  }, [filteredPages, visiblePageIds]);

  const displayStepIndex = React.useMemo(() => {
    if (!currentPageId || !visiblePageIds.has(currentPageId)) {
      return -1;
    }
    return orderedVisiblePages.findIndex((page) => page.id === currentPageId);
  }, [orderedVisiblePages, currentPageId, visiblePageIds]);

  // Get current page name for display
  const currentPageName = React.useMemo(() => {
    if (displayStepIndex < 0) return undefined;
    return orderedVisiblePages[displayStepIndex]?.name;
  }, [orderedVisiblePages, displayStepIndex]);

  // Report visible page info to parent
  useEffect(() => {
    if (onVisiblePagesChange) {
      onVisiblePagesChange({
        visiblePageCount,
        displayStepIndex,
        currentPageName,
      });
    }
  }, [onVisiblePagesChange, visiblePageCount, displayStepIndex, currentPageName]);

  const { trigger } = useFormContext();

  const { saveBlankPage } = useSaveBlankPageData({
    activeStepIndex,
  });

  const saveOptionsPage = useSaveOptionsPageData({
    activeStepIndex,
  });

  const saveFloorPlanPage = useSaveFloorPlanPageData({
    activeStepIndex,
  });

  const saveOffersPage = useSaveQuote({
    activeStepIndex,
  });

  const saveSummaryPage = useSaveSummaryPage({
    activeStepIndex,
  });
  const reconcileSkippedHiddenGuarantorPages = useReconcileSkippedHiddenGuarantorPages();
  const navigationInFlight = React.useRef(false);

  const { charges, scheduledChargesLoaded } = useSelector((state: RootState) => state.shoppingCart);
  const { currencyCode } = useSelector((state: RootState) => state.locale);

  const hasDueNowCharges = React.useMemo(() => {
    return hasDueNowPaymentRequired(charges, currencyCode);
  }, [charges, currencyCode]);

  /**
   * Set error based on page type
   * @param newPageIndex - the target page index
   * @returns
   */
  async function savePageResponses(newPageIndex: number) {
    await trigger();
    const currentStep = pages[activeStepIndex];
    const isMovingForward = newPageIndex > activeStepIndex;

    let response = true;
    switch (currentStep?.type) {
      case PageType.Blank:
        response = await saveBlankPage(newPageIndex);
        break;
      case PageType.Summary:
        response = await saveSummaryPage(isMovingForward);
        break;
      case PageType.Options:
        response = await saveOptionsPage(newPageIndex, addOns, selectedSpecials);
        break;
      case PageType.FloorPlans:
        response = await saveFloorPlanPage(newPageIndex);
        break;
      case PageType.Offers:
        await saveOffersPage(newPageIndex);
        break;
      default:
        break;
    }

    if (response) {
      await reconcileSkippedHiddenGuarantorPages(activeStepIndex, newPageIndex);
    }
    return response;
  }

  async function onStepClick(targetPageId: string, newPageIndex: number) {
    const activePageIndex = getActiveStepIndex();
    if (activePageIndex === newPageIndex) return;

    const newStep = pages[newPageIndex];

    // Skip payment page if there are no due now charges
    if (newStep?.type === PageType.Payment && scheduledChargesLoaded && !hasDueNowCharges) {
      const nextStep = pages[newPageIndex + 1];
      if (nextStep) {
        if (nextStep.id === pages[activePageIndex]?.id) return;
        if (navigationInFlight.current) return;
        navigationInFlight.current = true;
        onNavigationStart();
        let didNavigate = false;
        try {
          onClose();
          navigate(`/${orgId}/${propertyId}/${nextStep.id}`);
          didNavigate = true;
        } finally {
          navigationInFlight.current = false;
          if (!didNavigate) onNavigationEnd();
        }
        return;
      }
    }

    if (newStep?.type === PageType.Payment) {
      // Check if all steps are complete
      const incompleteSteps = leasingJourneyErrors.filter((error) => error !== 0);

      if (incompleteSteps.length > 0) {
        notification(
          t(
            SystemTranslationKeys.nav.pleaseCompleteAllSteps,
            'Please complete all the steps before making a payment.',
          ),
          NotificationStatus.WARNING,
        );
        return;
      }
    }

    if (navigationInFlight.current) return;
    navigationInFlight.current = true;
    onNavigationStart();
    let didNavigate = false;
    try {
      onClose();

      if (leasingJourneyErrors[activePageIndex] !== null) {
        await trigger();
      }
      const result = await savePageResponses(newPageIndex);
      if (result) {
        navigate(`/${orgId}/${propertyId}/${targetPageId}`);
        didNavigate = true;
      }
    } finally {
      navigationInFlight.current = false;
      if (!didNavigate) onNavigationEnd();
    }
  }

  const getPageTextColor = (index: number) => {
    const stepErrors = leasingJourneyErrors[index] ?? null;
    if (activeStepIndex === index) {
      return 'gray.900';
    }
    if (stepErrors !== null && stepErrors > 0) {
      return 'red.800';
    }
    if (stepErrors === 0) {
      return 'gray.800';
    }
    return 'gray.700';
  };

  return (
    <Box>
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        <Text fontSize="2xl" fontWeight="medium" lineHeight="18px">
          {tSystem(SystemTranslationKeys.nav.application)}
        </Text>
        {displayStepIndex >= 0 && (
          <Flex alignItems="center" fontSize="11px">
            {tSystem(SystemTranslationKeys.nav.stepOf, {
              current: displayStepIndex + 1,
              total: visiblePageCount,
            })}{' '}
            <ChevronUpIcon ml={1} />
          </Flex>
        )}
      </Flex>
      <List gap={10}>
        {filteredPages.map((page) => {
          // Find the original index in the full pages array
          const originalIndex = pages.findIndex((p) => p.id === page.id);
          return (
            <PageNavigationItem
              key={page.id}
              page={page}
              index={originalIndex}
              activeStepIndex={activeStepIndex}
              leasingJourneyErrors={leasingJourneyErrors}
              onStepClick={onStepClick}
              getPageTextColor={getPageTextColor}
              onVisibilityChange={handleVisibilityChange}
              isNavigationLoading={isNavigationLoading}
            />
          );
        })}
      </List>
    </Box>
  );
};

export default ApplicationNavigation;
